import { Button } from 'primereact/button'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocalState } from '../../../../shared/UseLocalState'
import {
  ApiStartersPostRequest,
  ApiStartersValidatePostRequest,
  ExamDto,
  Horse,
  Person,
  ResponseError,
  ServiceDto,
  ServiceDtoToJSON,
  Starter,
  StarterExmsDto,
  StarterServicesDto,
  StartersApi
} from '../../../../api'
import { actualShowState } from '../../../../state/ActualShow.state'
import { registrationState, ServiceDtoWithAmount } from '../Registration.state'
import { authState } from '../../../../state/Auth.state'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { ConfirmOverlay } from '../../../confirm-overlay/Confirm-overlay'
import { appState } from '../../../../state/App.state'
import { Loading } from '../../../loading/Loading'

// JK/TODO: send req overlay on warnings with force

const validateIban = (value: string): boolean => {
  const raw = value.replace(/\s/g, '').toUpperCase()
  if (raw.length === 0) return false
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/.test(raw)) return false
  const rearranged = raw.slice(4) + raw.slice(0, 4)
  const numeric = rearranged.replace(/[A-Z]/g, (c) => String(c.charCodeAt(0) - 55))
  let remainder = 0
  for (const chunk of numeric.match(/.{1,9}/g) ?? []) {
    remainder = Number(String(remainder) + chunk) % 97
  }
  return remainder === 1
}

export const ClosingScreen = () => {
  /* STATE */
  const activeScreen = useLocalState<number>(registrationState.activeScreen$, registrationState.activeScreen$.value)
  const selectedHorse = useLocalState<Horse | null>(registrationState.selectedHorse$, registrationState.selectedHorse$.value)
  const selectedUser = useLocalState<Person | null>(registrationState.selectedRider$, registrationState.selectedRider$.value)
  const selectedExams = useLocalState<ExamDto[]>(registrationState.selectedExams$, registrationState.selectedExams$.value)
  const selectedServices = useLocalState<ServiceDtoWithAmount[]>(
    registrationState.selectedServices$,
    registrationState.selectedServices$.value
  )
  const gweGateExam = useLocalState<number>(registrationState.gweGateExam$, registrationState.gweGateExam$.value)
  const actualShowServices = useLocalState<ServiceDto[]>(actualShowState.actualShowServices$, actualShowState.actualShowServices$.value)
  const booker = useLocalState<Person | null>(authState.user$, authState.user$.value)

  /* DERIVED STATE */
  const ibanValid = useMemo(() => validateIban(booker?.bankAccountIBAN ?? ''), [booker?.bankAccountIBAN])
  const bankOwnerValid = useMemo(() => (booker?.bankOwner?.trim().length ?? 0) > 0, [booker?.bankOwner])

  const [errors, setErrors] = useState<string[]>([])
  const [warnings, setWarnings] = useState<string[]>([])
  const [validated, setValidated] = useState(false)
  const [loading, setLoading] = useState(false)

  /* LOGIC */
  const validateRegistration = useCallback(
    async (id: number) => {
      try {
        setLoading(true)
        setErrors([])
        setWarnings([])

        // get data for services request
        const showId = actualShowState.actualShowNr$.value
        const bookerId = authState.user$.value?.registerId

        // validate services request
        if (!showId) throw new Error(`showId is mandatory`)
        if (!bookerId) throw new Error('ownerId is mandatory')

        // get data for starter request
        const horseRegisterId = selectedHorse?.registerId ?? ''
        const riderRegisterId = selectedUser?.registerId ?? ''

        const bookedExams: StarterExmsDto[] =
          selectedExams
            ?.filter((exam) => !!exam?.id)
            ?.map((exam) => ({ examId: exam?.id, gweGateExam: id === exam?.id }) as unknown as StarterExmsDto) ??
          [] ??
          []

        // handle exams to cancel
        const starter = registrationState.selectedStarter$.value
        const examsToCancel: StarterExmsDto[] =
          (starter as any)?.bookedExams?.filter((e: StarterExmsDto) => !bookedExams?.some((e2) => e?.examId === e2?.examId)) ?? []
        examsToCancel?.forEach((e) =>
          bookedExams.push({ examId: e?.examId, gweGateExam: e?.gweGateExam, cancelRequest: true } as StarterExmsDto)
        )

        if (bookedExams.length < 1 && selectedServices.length < 1) setErrors([`Sie haben nichts gebucht`])

        // validate starter request
        //if (!horseRegisterId) throw new Error('horse.registerId is mandatory')
        //if (!riderRegisterId) throw new Error('user.registerId is mandatory')

        /*if (!bookedExams || bookedExams?.length < 1) throw new Error(`bookedExams are mandatory`)
          if (!selectedServices || selectedServices?.length < 1)
          {
              appState.addToast('Sie müssen noch Servicese buchen', 'error')
              throw new Error(`bookedServices are mandatory`)
          }
          */
        const request: ApiStartersValidatePostRequest = {
          starterDto: {
            bookerId,
            showId,
            horseRegisterId,
            riderRegisterId,
            bookedExams,
            bookedServices: selectedServices?.map((service) => ({ serviceId: service.id, quantity: service?.amount })) ?? []
          }
        }
        // validate starter request at backend
        return new Promise<{ request: ApiStartersPostRequest | undefined; warnings: string[]; errors: string[] }>((resolve) =>
          new StartersApi(authState.apicfg())
            .apiStartersValidatePost(request)
            .then(() => {
              resolve({
                request,
                errors: [],
                warnings: []
              })
              setLoading(false)
            })
            .catch((err: ResponseError) => {
              try {
                err.response
                  .json()
                  .then((resp: { errors: string[]; warnings: [] }) => {
                    setErrors(resp?.errors ?? [])
                    setWarnings(resp?.warnings ?? [])
                    resolve({ request, errors: resp?.errors ?? [], warnings: resp?.warnings ?? [] })
                    setLoading(false)
                  })
                  .catch(() => {
                    setErrors([`Etwas lief schief!`])
                    setWarnings([])
                    resolve({ request, errors: [`Etwas lief schief!`], warnings: [] })
                    setLoading(false)
                  })
              } catch (err) {
                setErrors([`Etwas lief schief!`])
                setWarnings([])
                resolve({ request, errors: [`Etwas lief schief!`], warnings: [] })
                setLoading(false)
              }
            })
        )
      } catch (err) {
        console.error(err)
        setErrors(['Etwas lief schief'])
        setLoading(false)
      }
    },
    [selectedHorse, selectedUser, selectedExams, selectedServices]
  )

  const finishRegistration = useCallback(async (id: number) => {
    setLoading(true)
    const validation = await validateRegistration(id)
    if ((validation?.errors ?? [])?.length < 1 && validation?.request) {
      setLoading(true)
      new StartersApi(authState.apicfg())
        .apiStartersPostRaw(validation?.request)
        .then(() => {
          appState.addToast('Anmeldung erfolgreich!', 'success')
          window.location.hash = '#/home'
        })
        .catch(() => {
          appState.addToast('Anmeldung fehlgeschlagen!', 'error')
        })
        .finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [])

  const handleGweGateExamChange = useCallback((examId: number) => {
    registrationState.gweGateExam$.next(examId)
    validateRegistration(examId).then(() => setValidated(true))
  }, [])

  const combinedData = selectedExams.map((exam) => ({
    exam,
    starterExams: selectedExams.find((se) => se.id === exam.id) || { gweGateExam: false }
  }))

  function hidePrice(exam: ExamDto) {
    const starter = registrationState.selectedStarter$.value as any
    if (starter?.bookedExams?.some((e: StarterExmsDto) => e?.examId === exam?.id)) {
      return true
    }
    return false
  }

  /* EFFECTS */
  useEffect(() => {
    if (activeScreen === 5) {
      setValidated(false)
      validateRegistration(gweGateExam).then(() => setValidated(true))
    }
  }, [activeScreen])

  /* TEMPLATE */
  return (
    <>
      <Loading loading={loading} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <h2>Zusammenfassung</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <p>
            <b>Pferd</b>
            <br />
            <span>{selectedHorse?.name}</span>
          </p>
          <p>
            <b>Reiter</b>
            <br />
            <span>
              {selectedUser?.firstName} {selectedUser?.lastName} {selectedUser?.member}
            </span>
          </p>
          {<b>Services</b>}

          {/* Allgemeine Services (Typ G = Grundgebühr Reiter) */}
          {selectedServices?.some((s) => !s?.type || s?.type === 'G') && (
            <>
              <p style={{ margin: '0.25rem 0', fontStyle: 'italic', color: '#555' }}>Allgemein (Reiter)</p>
              <DataTable
                showHeaders={true}
                value={selectedServices
                  ?.filter((s) => !s?.type || s?.type === 'G')
                  ?.sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''))}
                emptyMessage='keine Services'
              >
                <Column header='Bezeichnung' body={(service: ServiceDto) => service?.name} />
                <Column header='Menge' body={(s: ServiceDtoWithAmount) => s?.amount} />
                <Column
                  header='Preis'
                  body={(service: ServiceDtoWithAmount) =>
                    ((service?.price ?? 0) * (service?.amount ?? 0))?.toFixed(2).replace('.', ',') + ' €'
                  }
                />
              </DataTable>
            </>
          )}

          {/* Startnummer-Services (Typ N, H, I) – neue Buchung */}
          {selectedServices?.some((s) => s?.type === 'N' || s?.type === 'H' || s?.type === 'I') && (
            <div style={{ marginTop: '0.5rem' }}>
              <p style={{ margin: '0.25rem 0', fontStyle: 'italic', color: '#555' }}>Startnummer-Services</p>
              <DataTable
                showHeaders={true}
                value={selectedServices
                  ?.filter((s) => s?.type === 'N' || s?.type === 'H' || s?.type === 'I')
                  ?.sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''))}
                emptyMessage='keine Services'
              >
                <Column header='Bezeichnung' body={(service: ServiceDto) => service?.name} />
                <Column header='Menge' body={(s: ServiceDtoWithAmount) => s?.amount} />
                <Column
                  header='Preis'
                  body={(service: ServiceDtoWithAmount) =>
                    ((service?.price ?? 0) * (service?.amount ?? 0))?.toFixed(2).replace('.', ',') + ' €'
                  }
                />
              </DataTable>
            </div>
          )}

          {selectedServices?.length < 1 && <p style={{ color: '#888' }}>keine Services</p>}

          <b>Prüfungen</b>
          <DataTable showHeaders={true} value={combinedData} emptyMessage='keine Prüfungen'>
            <Column body={(rowData: { exam: ExamDto; starterExams: StarterExmsDto }) => rowData.exam?.no} bodyStyle={{ width: '3rem' }} />
            <Column header='Prüfung' body={(rowData: { exam: ExamDto; starterExams: StarterExmsDto }) => rowData.exam?.name} />
            <Column
              header='Preis'
              body={(rowData: { exam: ExamDto; starterExams: StarterExmsDto }) => (
                  <span style={hidePrice(rowData?.exam) ? { textDecoration: 'line-through', visibility: 'hidden'} : {}}>
                  {selectedUser?.member
                    ? rowData.exam?.price?.toFixed(2).replace('.', ',') + ' €'
                    : rowData.exam?.priceNoMember?.toFixed(2).replace('.', ',') + ' €'}
                </span>
              )}
            />
            <Column header='GWE Typ' body={(rowData: { exam: ExamDto; starterExams: StarterExmsDto }) => rowData.exam?.gweType} />
            <Column
              header='GWE Gang Auswahl'
              body={(rowData: { exam: ExamDto; starterExams: StarterExmsDto }) =>
                rowData.exam?.gweType === 'G' ? (
                  <input
                    type='radio'
                    name='gweGateExam'
                    style={{ cursor: 'pointer' }}
                    checked={rowData?.exam?.id === gweGateExam}
                    onClick={(e) => handleGweGateExamChange(rowData.exam.id ?? 0)}
                  />
                ) : null
              }
            />
          </DataTable>
        </div>
        <div>
          {(warnings ?? [])?.map((w, i) => (
            <p key={i} style={{ color: '#e15501', fontWeight: 'bold' }}>
              {w}
            </p>
          ))}
        </div>
        <div>
          {(errors ?? [])?.map((e, i) => (
            <p key={i} style={{ color: '#e74c3c', fontWeight: 'bold' }}>
              {e}
            </p>
          ))}
        </div>
        {!ibanValid && (
          <p style={{ color: '#e74c3c', fontWeight: 'bold' }}>
            Bitte hinterlegen Sie eine gültige IBAN in Ihren Kontodaten (Adressänderung).
          </p>
        )}
        {!bankOwnerValid && (
          <p style={{ color: '#e74c3c', fontWeight: 'bold' }}>
            Bitte hinterlegen Sie einen Kontoinhaber in Ihren Kontodaten (Adressänderung).
          </p>
        )}
        <br />
        <Button className='button' onClick={() => finishRegistration(gweGateExam)} disabled={errors?.length > 0 || !validated || !ibanValid || !bankOwnerValid}>
          Zahlungspflichtig Buchen{warnings?.length > 0 && errors?.length < 1 ? ` (Trotz Warnungen)` : null}
        </Button>
      </div>
    </>
  )
}
